import { createSelector, createSlice } from "@reduxjs/toolkit";
import { remove } from 'aws-amplify/storage';
import { generateClient } from 'aws-amplify/api';
import { orderBy } from "lodash";
import {
    completedDocumentByCrewMemberId,
    completedDocumentByCustomerId,
    completedDocumentByLocationId,
    listDocuments,
    searchDocuments,
    searchCompletedDocuments,
    completedDocumentByForemanId
} from "../graphql/customQueries";
import { createCompletedDocument, createDocument, deleteCompletedDocument, deleteDocument, sendDocumentByEmail, startLongProcess, updateCompletedDocument, updateDocument } from "../graphql/mutations";
import { documentsByFolderId, getCompletedDocument, getDocument } from "../graphql/queries";
import { selectUser } from "./auth";
import { popPreviousToken, pushPreviousToken, reset, resetPagination } from "./globalActions";
import { selectUser as selectUserProfile } from "./user";

const client = generateClient();

const moduleName = "document";

const initialState = {
    loading: false,
    documents: [],
    document: null,
    sharedDocuments: [],
    folders: {},
    nextToken: null,
    previousTokens: [],
};

const userSlice = createSlice({
    name: moduleName,
    initialState,
    extraReducers: (builder) => {
        builder.addCase(reset, () => initialState);
        builder.addCase(resetPagination, (state) => {
            state.nextToken = null;
            state.previousTokens = [];
        });
        builder.addCase(popPreviousToken, (state) => {
            state.previousTokens.pop();
        });
        builder.addCase(pushPreviousToken, (state, action) => {
            state.previousTokens.push(action.payload);
        });
    },
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setDocuments: (state, action) => {
            state.documents = action.payload.items;
            state.nextToken = action.payload.nextToken;
        },
        setFolderDocuments: (state, action) => {
            state.folders[action.payload.folderId] = action.payload.documents;
        },
        onDocumentChange: (state, action) => {
            let ctx = state.folders[action.payload.folderId];
            if (ctx) {
                let idx = ctx.findIndex((d) => d.id === action.payload.id);
                if (idx >= 0) {
                    ctx[idx] = action.payload;
                } else {
                    ctx.push(action.payload);
                }
            }
            state.document = action.payload;
        },
        onDeleteDocument: (state, action) => {
            let idx = state.documents.findIndex((d) => d.id === action.payload.id);
            if (idx >= 0) {
                state.documents = [...state.documents.slice(0, idx), ...state.documents.slice(idx + 1)];
            }
        },
        setActiveDocument: (state, action) => {
            state.document = action.payload;
        },
        setSharedDocuments: (state, action) => {
            state.sharedDocuments = action.payload.items;
        },
    },
});

export const { setLoading, setDocuments, onDocumentChange, onDeleteDocument, setFolderDocuments, setActiveDocument, setSharedDocuments } = userSlice.actions;
export default userSlice.reducer;

export const selectState = (state) => state[moduleName];
export const selectLoading = createSelector(selectState, (state) => state.loading);
export const selectDocuments = createSelector(selectState, (state) => state.documents);
export const selectSharedDocuments = createSelector(selectState, (state) => state.sharedDocuments);
export const selectDocument = createSelector(selectState, (state) => state.document);
export const selectPagination = createSelector(selectState, (state) => ({ nextToken: state.nextToken, previousTokens: state.previousTokens }));
export const selectDocumentsByFolder = (folderId) => createSelector(selectState, (state) => orderBy(state.folders[folderId], (d) => d.name?.toLowerCase()));

export const fetchCompletedDocuments =
    (locationId, limit = 12, startDate, endDate, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                dispatch(setLoading(true));
                if (storeNextToken) dispatch(pushPreviousToken(nextToken));
                else dispatch(popPreviousToken());

                let createdAt = undefined;
                if (startDate && endDate) {
                    createdAt = { between: [startDate, endDate] };
                }

                const { data } = await client.graphql({
                    query: completedDocumentByLocationId,
                    variables: {
                        limit,
                        nextToken,
                        locationId,
                        createdAt,
                        sortDirection: "DESC",
                    },
                });
                dispatch(setDocuments(data.completedDocumentByLocationId));
                return data.completedDocumentByLocationId.items;
            } finally {
                dispatch(setLoading(false));
            }
        };

export const fetchCompletedDocumentsByUser =
    (userId, limit = 12, startDate, endDate, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                dispatch(setLoading(true));
                if (storeNextToken) dispatch(pushPreviousToken(nextToken));
                else dispatch(popPreviousToken());

                let createdAt = undefined;
                if (startDate && endDate) {
                    createdAt = { between: [startDate, endDate] };
                }

                const { data } = await client.graphql({
                    query: completedDocumentByCrewMemberId,
                    variables: {
                        limit,
                        nextToken,
                        createdBy: userId,
                        createdAt,
                        sortDirection: "DESC",
                    },
                });
                dispatch(setDocuments(data.completedDocumentByCrewMemberId));
                return data.completedDocumentByCrewMemberId.items;
            } finally {
                dispatch(setLoading(false));
            }
        };

export const fetchCompletedDocumentsByForeman =
    (foremanId, limit = 12, startDate, endDate, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                dispatch(setLoading(true));
                if (storeNextToken) dispatch(pushPreviousToken(nextToken));
                else dispatch(popPreviousToken());

                let createdAt = undefined;
                if (startDate && endDate) {
                    createdAt = { between: [startDate, endDate] };
                }

                const { data } = await client.graphql({
                    query: completedDocumentByForemanId,
                    variables: {
                        limit,
                        nextToken,
                        foremanId,
                        createdAt,
                        sortDirection: "DESC",
                    },
                });
                dispatch(setDocuments(data.completedDocumentByForemanId));
                return data.completedDocumentByForemanId.items;
            } finally {
                dispatch(setLoading(false));
            }
        };

export const fetchCompletedDocumentsByCustomer =
    (customerId, limit = 12, startDate, endDate, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                dispatch(setLoading(true));
                if (storeNextToken) dispatch(pushPreviousToken(nextToken));
                else dispatch(popPreviousToken());

                let createdAt = undefined;
                if (startDate && endDate) {
                    createdAt = { between: [startDate, endDate] };
                }

                const { data } = await client.graphql({
                    query: completedDocumentByCustomerId,
                    variables: {
                        limit,
                        nextToken,
                        customerId,
                        createdAt,
                        sortDirection: "DESC",
                    },
                });
                dispatch(setDocuments(data.completedDocumentByCustomerId));
                return data.completedDocumentByCustomerId.items;
            } finally {
                dispatch(setLoading(false));
            }
        };

export const fetchSharedDocumentsByUser =
    (userId, locationId, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                dispatch(setLoading(true));
                if (storeNextToken) dispatch(pushPreviousToken(nextToken));
                else dispatch(popPreviousToken());

                const { data } = await client.graphql({
                    query: completedDocumentByLocationId,
                    variables: {
                        locationId,
                        nextToken,
                        filter: { authors: { contains: userId } },
                        sortDirection: "DESC",
                    },
                });
                dispatch(setSharedDocuments(data.completedDocumentByLocationId));
                return data.completedDocumentByLocationId.items;
            } finally {
                dispatch(setLoading(false));
            }
        };

export const fetchDocuments = () => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: listDocuments,
        });
        dispatch(setDocuments(data.listDocuments));
        return data.listDocuments.items;
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchDocument = (id) => async (dispatch) => {
    const { data } = await client.graphql({
        query: getDocument,
        variables: {
            id,
        },
    });
    if (!data.getDocument) { throw new Error("Document doesn't exist") }
    dispatch(setActiveDocument(data.getDocument));
    return data.getDocument;
};

export const fetchCompletedDocument = (id) => async (dispatch) => {
    const { data } = await client.graphql({
        query: getCompletedDocument,
        variables: {
            id,
        },
    });
    if (!data.getCompletedDocument) { throw new Error("Completed document doesn't exist") }
    dispatch(setActiveDocument(data.getCompletedDocument));
    return data.getCompletedDocument;
};

export const fetchDocumentsByFolder = (folderId) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: documentsByFolderId,
            variables: {
                folderId,
            },
        });
        dispatch(setFolderDocuments({ folderId, documents: data.documentsByFolderId.items }));
        return data.documentsByFolderId.items;
    } finally {
        dispatch(setLoading(false));
    }
};

export const findDocuments = (search) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: searchDocuments,
            variables: {
                filter: {
                    or: [{ summary: { matchPhrasePrefix: search } }, { name: { matchPhrasePrefix: search } }],
                },
                sort: [{ field: "name", direction: "asc" }],
            },
        });
        dispatch(setDocuments(data.searchDocuments));
        return data.searchDocuments.items;
    } finally {
        dispatch(setLoading(false));
    }
};

export const findCompletedDocuments =
    (locationId, search, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                dispatch(setLoading(true));
                if (storeNextToken) dispatch(pushPreviousToken(nextToken));
                else dispatch(popPreviousToken());

                const { data } = await client.graphql({
                    query: searchCompletedDocuments,
                    variables: {
                        nextToken,
                        filter: {
                            or: [
                                { annotations: { wildcard: `*${search}*` } },
                                { name: { wildcard: `*${search}*` } }
                            ],
                            and: [{ locationId: { eq: locationId } }]
                        },
                        sort: [{ field: "createdAt", direction: "desc" }],
                        limit: 12,
                    },
                });
                dispatch(setDocuments(data.searchCompletedDocuments));
                return data.searchCompletedDocuments.items;
            } finally {
                dispatch(setLoading(false));
            }
        };

export const newDocument = (document) => async (dispatch, getState) => {
    try {
        dispatch(setLoading(true));
        const user = selectUser(getState());
        const timestamp = new Date().toISOString();
        const { data } = await client.graphql({
            query: createDocument,
            variables: {
                input: {
                    ...document,
                    createdAt: timestamp,
                    updatedAt: timestamp,
                    createdBy: user.username,
                    updatedBy: user.username,
                },
            },
        });
        dispatch(onDocumentChange(data.createDocument));
        return data.createDocument;
    } finally {
        dispatch(setLoading(false));
    }
};

export const modifyDocument =
    ({ id, name, summary, fillable, folderId, daysUntilFinalized }) =>
        async (dispatch, getState) => {
            try {
                dispatch(setLoading(true));
                const user = selectUser(getState());
                const timestamp = new Date().toISOString();
                const { data } = await client.graphql({
                    query: updateDocument,
                    variables: {
                        input: {
                            id,
                            name,
                            summary,
                            fillable,
                            folderId,
                            daysUntilFinalized,
                            updatedAt: timestamp,
                            updatedBy: user.username,
                        },
                    },
                });
                dispatch(onDocumentChange(data.updateDocument));
                return data.updateDocument;
            } finally {
                dispatch(setLoading(false));
            }
        };

export const removeDocument = (document) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: deleteDocument,
            variables: {
                input: {
                    id: document.id,
                },
            },
        });
        await remove({ path: `public/${document.pdfKey}` });
        dispatch(onDeleteDocument(document));
    } finally {
        dispatch(setLoading(false));
    }
};

export const removeCompletedDocument = (document) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: deleteCompletedDocument,
            variables: {
                input: {
                    id: document.id,
                },
            },
        });
        await remove({ path: `public/${document.pdfKey}` });
        dispatch(onDeleteDocument(document));
    } finally {
        dispatch(setLoading(false));
    }
};

export const newCompletedDocument = (document) => async (dispatch, getState) => {
    try {
        console.log('creating new document', document.id);
        dispatch(setLoading(true));
        const user = selectUserProfile(getState());
        const timestamp = new Date().toISOString();

        const { data } = await client.graphql({
            query: createCompletedDocument,
            variables: {
                input: {
                    ...document,
                    createdAt: timestamp,
                    updatedAt: timestamp,
                    createdBy: user.crewMemberId,
                    updatedBy: user.crewMemberId,
                },
            },
        });
        dispatch(onDocumentChange(data.createCompletedDocument));
        return data.createCompletedDocument;
    } finally {
        dispatch(setLoading(false));
    }
};

export const lockCompletedDocument =
    (id, lockedById) =>
        async (dispatch, getState) => {
            try {
                // dispatch(setLoading(true));
                const user = selectUserProfile(getState());
                const timestamp = new Date().toISOString();
                const { data } = await client.graphql({
                    query: updateCompletedDocument,
                    variables: {
                        input: {
                            id,
                            lockedById,
                            updatedAt: timestamp,
                            updatedBy: user.crewMemberId,
                        },
                    },
                });
                // dispatch(onDocumentChange(data.updateCompletedDocument));
                return data.updateCompletedDocument;
            } finally {
                // dispatch(setLoading(false));
            }
        };

export const unlockCompletedDocument = async (id) => {
    try {
        // dispatch(setLoading(true));
        const timestamp = new Date().toISOString();
        const { data } = await client.graphql({
            query: updateCompletedDocument,
            variables: {
                input: {
                    id,
                    lockedById: null,
                    updatedAt: timestamp,
                },
            },
        });
        // dispatch(onDocumentChange(data.updateCompletedDocument));
        return data.updateCompletedDocument;
    } finally {
        // dispatch(setLoading(false));
    }
};

export const modifyCompletedDocument =
    ({ id, draft = undefined, authors, annotations, customerId, locked, lockedById, foremanId }) =>
        async (dispatch, getState) => {
            try {
                console.log("updating existing document", id);
                dispatch(setLoading(true));
                const user = selectUserProfile(getState());
                const timestamp = new Date().toISOString();
                const { data } = await client.graphql({
                    query: updateCompletedDocument,
                    variables: {
                        input: {
                            id,
                            draft,
                            locked,
                            lockedById,
                            authors,
                            customerId,
                            foremanId,
                            annotations,
                            updatedAt: timestamp,
                            updatedBy: user.crewMemberId,
                        },
                    },
                });
                dispatch(onDocumentChange(data.updateCompletedDocument));
                return data.updateCompletedDocument;
            } finally {
                dispatch(setLoading(false));
            }
        };

export const emailDocument = (documentId, to, cc, bcc, message) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data: { sendDocumentByEmail: response } } = await client.graphql({
            query: sendDocumentByEmail,
            variables: {
                input: {
                    documentId,
                    to,
                    cc,
                    bcc,
                    message,
                },
            },
        });
        if (!response.status) throw new Error(response.message);
        return response;
    } finally {
        dispatch(setLoading(false));
    }
};

const runLongProcess = (processName, params) => async (dispatch) => {
    const { data } = await client.graphql({
        query: startLongProcess,
        variables: {
            input: {
                processName,
                params: JSON.stringify(params)
            },
        },
    });
    return data.startLongProcess;
}

export const fetchForemanFormReport = (params) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { status, message } = await runLongProcess("foremanFormReport", params)(dispatch)
        if (!status) throw new Error(message);
    } finally {
        dispatch(setLoading(false));
    }
};
